import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Headphones, Clock, AlertCircle, CheckCircle2, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const ServicePolicy = () => {
  const severityLevels = [
    {
      level: "Severity 1 – Critical",
      description: "Complete service outage or major fleet-wide impact",
      response: "4 business hours",
      resolution: "1-2 business days",
      color: "red"
    },
    {
      level: "Severity 2 – High",
      description: "Significant service degradation or partial fleet impact",
      response: "8 business hours",
      resolution: "2-3 business days",
      color: "orange"
    },
    {
      level: "Severity 3 – Medium",
      description: "Individual vehicle issue or intermittent tracking",
      response: "1 business day",
      resolution: "3-5 business days",
      color: "yellow"
    },
    {
      level: "Severity 4 – Low",
      description: "General queries, reports, configuration, or advisory requests",
      response: "2 business days",
      resolution: "As scheduled",
      color: "blue"
    }
  ];

  const services = [
    "GPS tracking device installation",
    "Fleet tracking platform access and support",
    "SIRA certification (new and renewal)",
    "Device diagnostics and troubleshooting",
    "Device replacements (under warranty)",
    "Technical and operational support"
  ];

  const customerResponsibilities = [
    "Provide accurate vehicle and contact details",
    "Ensure vehicle availability for scheduled service visits",
    "Respond promptly to TrackOn requests for information",
    "Maintain active subscription and payment status",
    "Avoid unauthorized tampering with GPS devices"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_099c2006-67c6-4d2e-acf9-1e0fd2c8352f/artifacts/80f6u7mg_Logo%20Final%20Whole%202026.png" 
                alt="TrackOn Logo" 
                className="h-10 object-contain"
              />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-red-600 to-red-800 text-white py-16">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <Headphones className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Service Policy</h1>
          <p className="text-xl text-red-100">
            Mohammad Alnooryani Vehicles Tracking Systems Services
          </p>
          <p className="mt-2 text-red-200">Comprehensive service standards & support framework</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-8 py-12">
        {/* Scope */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Scope of Services</h2>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">This Service Policy applies to all active and paid subscriptions. TrackOn provides the following services:</p>
              <div className="grid md:grid-cols-2 gap-3">
                {services.map((service, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{service}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> This policy only applies to customers with <strong>active and paid subscriptions</strong>. Services may be suspended for overdue accounts.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Operating Hours */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Operating Hours</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200 border-2">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-xl text-gray-900">Standard Hours</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-700"><strong>Days:</strong> Monday to Saturday</p>
                  <p className="text-gray-700"><strong>Time:</strong> 09:00 AM - 05:00 PM (UAE Time)</p>
                  <p className="text-sm text-gray-600 mt-3">* Excluding UAE public holidays</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="h-8 w-8 text-orange-600" />
                  <h3 className="font-bold text-xl text-gray-900">Outside Business Hours</h3>
                </div>
                <p className="text-gray-700">
                  Service requests received outside business hours will be logged and acknowledged on the next business day, unless covered under a separate agreement.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Support Channels */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Request & Support Channels</h2>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">Customers can raise service requests through:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-white p-4 rounded-lg">
                  <div className="bg-red-600 rounded-full w-10 h-10 flex items-center justify-center text-white font-bold">📧</div>
                  <div>
                    <p className="font-semibold text-gray-900">Official TrackOn Email</p>
                    <a href="mailto:gps@nooryanivts.com" className="text-red-600 hover:underline">gps@nooryanivts.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white p-4 rounded-lg">
                  <div className="bg-red-600 rounded-full w-10 h-10 flex items-center justify-center text-white font-bold">📞</div>
                  <div>
                    <p className="font-semibold text-gray-900">Phone / WhatsApp</p>
                    <a href="tel:+971508607793" className="text-red-600 hover:underline">+971 50 860 7793</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white p-4 rounded-lg">
                  <div className="bg-red-600 rounded-full w-10 h-10 flex items-center justify-center text-white font-bold">👤</div>
                  <div>
                    <p className="font-semibold text-gray-900">Designated Account Manager</p>
                    <p className="text-gray-600 text-sm">For enterprise customers</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                All service requests are logged, tracked, and assigned a unique reference number for follow-up.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* SLA */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Level Agreement (SLA)</h2>
          <p className="text-gray-700 mb-6">
            TrackOn commits to the following response and resolution time targets based on issue severity:
          </p>
          <div className="space-y-4">
            {severityLevels.map((severity, idx) => {
              const colorClasses = {
                red: "border-red-500 bg-red-50",
                orange: "border-orange-500 bg-orange-50",
                yellow: "border-yellow-500 bg-yellow-50",
                blue: "border-blue-500 bg-blue-50"
              };
              
              return (
                <Card key={idx} className={`border-l-4 ${colorClasses[severity.color]}`}>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-4 gap-4 items-center">
                      <div className="md:col-span-2">
                        <h3 className="font-bold text-lg text-gray-900 mb-1">{severity.level}</h3>
                        <p className="text-gray-600 text-sm">{severity.description}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Response Time</p>
                        <p className="font-bold text-gray-900">{severity.response}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Resolution Target</p>
                        <p className="font-bold text-gray-900">{severity.resolution}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="mt-6 bg-yellow-50 p-4 rounded-lg border border-yellow-300">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> Resolution timelines are best-effort targets and may vary based on issue complexity, vehicle accessibility, telecom network conditions, or third-party dependencies.
            </p>
          </div>
        </section>

        {/* Installation Standards */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Installation & Field Service Standards</h2>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">New GPS Installation</h3>
                    <p className="text-gray-700">Timeline: <strong>1-3 business days</strong></p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">On-Site Diagnostics/Inspection</h3>
                    <p className="text-gray-700">Timeline: <strong>2-4 business days</strong></p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-purple-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Device Replacement (Warranty Eligible)</h3>
                    <p className="text-gray-700">Subject to diagnosis & stock availability</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                <strong>Note:</strong> Installation timelines depend on vehicle readiness, site accessibility, and customer coordination.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* SIRA Certification */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">SIRA Certification Service</h2>
          <Card className="border-purple-200 border-2">
            <CardContent className="p-6">
              <div className="space-y-4">
                <p className="text-gray-700">
                  SIRA certification and renewal services are subject to SIRA authority approval timelines:
                </p>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>Typical Processing:</strong> 1-2 business days (subject to SIRA approval)</p>
                  <p className="text-gray-700 mb-2"><strong>Clarifications Required:</strong> Dependent on authority response time</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-600">
                  <p className="text-sm text-gray-700">
                    <strong>Important:</strong> TrackOn is not responsible for delays caused by regulatory processing times or incomplete customer documentation submitted to authorities.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Customer Responsibilities */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Customer Responsibilities</h2>
          <Card className="bg-blue-50">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">To ensure efficient service delivery, customers must:</p>
              <div className="space-y-3">
                {customerResponsibilities.map((responsibility, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="bg-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-gray-700">{responsibility}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-white p-4 rounded-lg border border-blue-300">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Failure to meet these responsibilities may result in service delays or SLA exclusions.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Service Exclusions */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Exclusions</h2>
          <Card className="border-red-200">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">TrackOn is not responsible for service disruptions caused by:</p>
              <div className="space-y-3">
                {[
                  "Mobile network or telecom provider outages",
                  "GPS signal obstruction or environmental interference",
                  "Force majeure events (natural disasters, civil unrest, etc.)",
                  "Third-party API or platform failures",
                  "Delays due to customer non-cooperation or unavailability"
                ].map((exclusion, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{exclusion}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Escalation */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Escalation Mechanism</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { level: "Level 1", title: "Support Team", desc: "Initial point of contact" },
              { level: "Level 2", title: "Technical Lead", desc: "Complex technical issues" },
              { level: "Level 3", title: "Management", desc: "Critical escalations" }
            ].map((escalation, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="bg-red-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold">{idx + 1}</span>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{escalation.title}</h3>
                  <p className="text-gray-600 text-sm">{escalation.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Legal */}
        <section className="mb-12">
          <Card className="bg-gray-100">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-gray-900 mb-3">Service Credits & Liability</h3>
              <p className="text-gray-700 text-sm mb-4">
                TrackOn provides services on a best-effort basis. No service credits, financial penalties, or compensation will be provided for missed SLA targets unless explicitly agreed in a separate written agreement. SLA timelines are targets and not guaranteed outcomes.
              </p>
              <h3 className="font-bold text-lg text-gray-900 mb-3 mt-6">Governing Law</h3>
              <p className="text-gray-700 text-sm">
                This Service Policy is governed by the laws of the <strong>United Arab Emirates</strong>. Disputes shall be subject to the exclusive jurisdiction of Dubai courts.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Contact CTA */}
        <Card className="bg-gradient-to-br from-red-600 to-red-800 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Need Service Support?</h3>
            <p className="mb-6 text-red-100">
              Our support team is ready to assist you
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+971508607793">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                  Call: +971 50 860 7793
                </Button>
              </a>
              <a href="mailto:gps@nooryanivts.com">
                <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100">
                  Email: gps@nooryanivts.com
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 TrackOn - Mohammad Alnooryani Vehicles Tracking Systems Services. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/warranty-policy" className="text-gray-400 hover:text-white">Warranty Policy</Link>
            <Link to="/service-policy" className="text-red-400 hover:text-red-300">Service Policy</Link>
            <Link to="/refund-policy" className="text-gray-400 hover:text-white">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicePolicy;
